// Secure Cookie Service - PCI Compliant with localhost support
class SecureCookieService {
  constructor() {
    this.isLocalhost = this.detectLocalhost();
    this.isSecureContext = this.detectSecureContext();
  }

  detectLocalhost() {
    return window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1' ||
           window.location.hostname.endsWith('.local');
  }

  detectSecureContext() {
    return window.location.protocol === 'https:' || this.isLocalhost;
  }

  // Set secure cookie with PCI compliance
  setSecure(name, value, options = {}) {
    const defaults = {
      maxAge: 86400, // 24 hours
      path: '/',
      sameSite: 'Strict'
    };

    const config = { ...defaults, ...options };
    let cookieString = `${name}=${value}`;

    // Path
    if (config.path) cookieString += `; Path=${config.path}`;

    // Max Age
    if (config.maxAge) cookieString += `; Max-Age=${config.maxAge}`;

    // Secure flag - only in production HTTPS
    if (this.isSecureContext && !this.isLocalhost) {
      cookieString += '; Secure';
    }

    // SameSite - always set for CSRF protection
    if (config.sameSite) cookieString += `; SameSite=${config.sameSite}`;

    document.cookie = cookieString;
  }

  // Legacy method for existing guid_token usage (ACTIVE PAGES ONLY)
  setCookie(name, value, options = {}) {
    // Map existing guid_token to secure session token
    if (name === 'guid_token') {
      this.setSessionToken(value);
      return;
    }
    
    // For other cookies, use secure method
    this.setSecure(name, value, options);
  }

  // Handle cookie configuration from central proxy
  applyCookieConfig(cookieConfig, tokenValue) {
    if (!cookieConfig || !tokenValue) return;
    
    this.setSecure('session_token', tokenValue, {
      maxAge: cookieConfig.maxAge || 900,
      secure: cookieConfig.secure,
      sameSite: cookieConfig.sameSite || 'Strict',
      path: '/'
    });
  }

  // Get cookie value
  get(name) {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [key, value] = cookie.trim().split('=');
      if (key === name) return value;
    }
    return null;
  }

  // Remove cookie securely
  remove(name, path = '/') {
    document.cookie = `${name}=; Path=${path}; Max-Age=0`;
  }

  // Set session token (most secure possible)
  setSessionToken(token) {
    this.setSecure('session_token', token, {
      maxAge: 900, // 15 minutes
      sameSite: 'Strict'
    });
  }

  // Get session token from secure cookie
  getSessionToken() {
    return this.get('session_token');
  }

  // Set remember device token
  setRememberDevice(deviceId) {
    this.setSecure('remember_device', deviceId, {
      maxAge: 2592000, // 30 days
      sameSite: 'Strict'
    });
  }

  // Clear all auth cookies including legacy ones
  clearAuth() {
    this.remove('session_token');
    this.remove('remember_device');
    this.remove('auth_token');
    this.remove('brand_context');
    this.remove('guid_token'); // Legacy cookie
    this.remove('cookie_choice_made');
  }

  // Get security headers for API calls
  getSecurityHeaders() {
    return {
      'X-Requested-With': 'XMLHttpRequest',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache'
    };
  }
}

export default new SecureCookieService();